import type {
  AuthResult,
  User,
  Conversation,
  ConversationDetail,
  FileItem,
  ModelsResponse,
  ModelVisibilityResponse,
  SystemPromptResponse,
  PromptVersion,
  SubagentListResponse,
  SubagentConfig,
  BatchUploadResponse,
  BatchRunConfig,
  BatchTask,
  UserProfile,
  SSECallbacks,
  ChatOptions,
  WorkspaceProcess,
} from '../types';

const BASE = '/api';

export function listWorkspaceLocks(): Promise<{ processes: WorkspaceProcess[] }> {
  return request<{ processes: WorkspaceProcess[] }>('GET', '/workspace/locks');
}

export function releaseWorkspaceLock(owner: string): Promise<{ status: string; owner?: string }> {
  return request<{ status: string; owner?: string }>('POST', '/workspace/locks/release', { owner });
}

export function getToken(): string {
  return localStorage.getItem('token') || '';
}

export function setToken(token: string): void {
  localStorage.setItem('token', token);
}

export function clearToken(): void {
  localStorage.removeItem('token');
}

function currentAcceptLanguage(): string {
  // Prefer the i18n-resolved language so backend messages match the UI
  // even before the user has clicked the switcher (i18n picks from
  // localStorage / navigator on init). Falls back to navigator.language
  // for ultra-early callers (rare) and finally 'zh'.
  try {
    const stored = localStorage.getItem('jf-lang');
    if (stored) return stored.toLowerCase().startsWith('en') ? 'en' : 'zh';
  } catch { /* private mode */ }
  const nav = (typeof navigator !== 'undefined' && navigator.language) || 'zh';
  return nav.toLowerCase().startsWith('en') ? 'en' : 'zh';
}

export async function request<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${getToken()}`,
    'Accept-Language': currentAcceptLanguage(),
  };

  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const config: RequestInit = {
    method,
    headers,
    ...options,
  };

  if (body) {
    config.body = body instanceof FormData ? body : JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, config);

  if (res.status === 401) {
    const err401 = await res.json().catch(() => ({ detail: '认证失败' }));
    if (getToken()) {
      clearToken();
      window.location.reload();
    }
    throw new Error(err401.detail || '认证失败，请重新登录');
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: '请求失败' }));
    throw new Error(err.detail || '请求失败');
  }

  return res.json();
}

// ===== Auth =====

export async function register(
  username: string,
  password: string,
  regKey: string,
): Promise<AuthResult> {
  return request('POST', '/auth/register', { username, password, reg_key: regKey });
}

export async function login(username: string, password: string): Promise<AuthResult> {
  return request('POST', '/auth/login', { username, password });
}

export async function getMe(): Promise<User> {
  return request('GET', '/auth/me');
}

// ===== Conversations =====

export async function listConversations(): Promise<Conversation[]> {
  return request('GET', '/conversations');
}

export async function createConversation(title = '新对话'): Promise<Conversation> {
  return request('POST', '/conversations', { title });
}

export async function getConversation(convId: string): Promise<ConversationDetail> {
  return request('GET', `/conversations/${convId}`);
}

export async function deleteConversation(convId: string): Promise<void> {
  return request('DELETE', `/conversations/${convId}`);
}

// ===== Chat (SSE) =====

let _currentAbortController: AbortController | null = null;

export function abortStream(): void {
  if (_currentAbortController) {
    _currentAbortController.abort();
    _currentAbortController = null;
  }
}

function handleSSEStream(res: Response, callbacks: SSECallbacks): void {
  const {
    onToken, onThinking, onToolCall, onToolCallChunk, onToolResult,
    onDone, onError, onInterrupt, onAutoApprove, onWorkspaceLock,
    onSubagentCall, onSubagentCallChunk, onSubagentStart, onSubagentToken,
    onSubagentThinking,     onSubagentToolCall, onSubagentToolChunk,
    onSubagentToolResult, onSubagentEnd,
    onRunContinued,
  } = callbacks;

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  (async () => {
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop()!;

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);
            switch (data.type) {
              case 'token':              onToken?.(data.content); break;
              case 'thinking':           onThinking?.(data.content); break;
              case 'interrupt':          onInterrupt?.(data.actions, data.configs); _currentAbortController = null; return;
              case 'auto_approve':       onAutoApprove?.(data.count, data.actions); break;
              case 'workspace_lock':     onWorkspaceLock?.(data.mode, data.granted || [], data.conflicts || []); break;
              case 'tool_call':          onToolCall?.(data.name, data.args); break;
              case 'tool_call_chunk':    onToolCallChunk?.(data.args_delta); break;
              case 'tool_result':        onToolResult?.(data.name, data.content); break;
              case 'subagent_call':      onSubagentCall?.(data.name, data.task, data.subagent_id); break;
              case 'subagent_call_chunk': onSubagentCallChunk?.(data.args_delta); break;
              case 'subagent_start':     onSubagentStart?.(data.name, data.subagent_id); break;
              case 'subagent_token':     onSubagentToken?.(data.content, data.agent, data.subagent_id); break;
              case 'subagent_thinking':  onSubagentThinking?.(data.content, data.agent, data.subagent_id); break;
              case 'subagent_tool_call': onSubagentToolCall?.(data.name, data.args, data.agent, data.subagent_id); break;
              case 'subagent_tool_chunk': onSubagentToolChunk?.(data.args_delta); break;
              case 'subagent_tool_result': onSubagentToolResult?.(data.name, data.content, data.agent, data.subagent_id); break;
              case 'subagent_end':       onSubagentEnd?.(data.name, data.result, data.subagent_id); break;
              case 'run_continued':      onRunContinued?.(data.content, data.queue_id); break;
              case 'done':              onDone?.(); _currentAbortController = null; return;
              case 'error':             onError?.(data.content); _currentAbortController = null; return;
            }
          } catch { /* ignore JSON parse error */ }
        }
      }

      if (buffer.startsWith('data: ')) {
        try {
          const data = JSON.parse(buffer.slice(6).trim());
          if (data.type === 'token') onToken?.(data.content);
        } catch { /* ignore */ }
      }

      onDone?.();
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      onError?.(e instanceof Error ? e.message : '连接中断');
    } finally {
      _currentAbortController = null;
    }
  })();
}

export function streamChat(
  conversationId: string,
  message: string | unknown[],
  callbacks: SSECallbacks,
  options: ChatOptions = {},
): void {
  abortStream();

  const controller = new AbortController();
  _currentAbortController = controller;

  const body: Record<string, unknown> = { conversation_id: conversationId, message };
  if (options.model) body.model = options.model;
  if (options.capabilities) body.capabilities = options.capabilities;
  if (options.plan_mode) body.plan_mode = true;
  if (options.yolo) body.yolo = true;
  if (options.lock_mode) body.lock_mode = options.lock_mode;
  if (options.lock_paths) body.lock_paths = options.lock_paths;

  fetch(`${BASE}/chat`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: '请求失败' }));
        callbacks.onError?.(err.detail || '请求失败');
        return;
      }
      handleSSEStream(res, callbacks);
    })
    .catch((err) => {
      if (err.name === 'AbortError') return;
      callbacks.onError?.(err.message);
    });
}

export async function stopChat(
  conversationId: string,
  opts?: { followUp?: string | unknown[]; queueId?: string; keepStream?: boolean },
): Promise<void> {
  if (!opts?.keepStream) {
    abortStream();
  }
  const body: Record<string, unknown> = { conversation_id: conversationId };
  if (opts?.followUp !== undefined) body.follow_up = opts.followUp;
  if (opts?.queueId) body.queue_id = opts.queueId;
  await request('POST', '/chat/stop', body).catch(() => {});
}

export interface StreamingStatusResult {
  streaming: string[];
  interrupted: string[];
}

export async function getStreamingStatus(): Promise<StreamingStatusResult> {
  const res = await request<StreamingStatusResult>('GET', '/chat/streaming-status');
  return { streaming: res.streaming ?? [], interrupted: res.interrupted ?? [] };
}

export interface InterruptStateResult {
  has_interrupt: boolean;
  actions?: unknown[];
  configs?: unknown;
}

export async function getInterruptState(conversationId: string): Promise<InterruptStateResult> {
  return request<InterruptStateResult>('GET', `/chat/interrupt/${conversationId}`);
}

export function resumeChat(
  conversationId: string,
  decisions: unknown[],
  callbacks: SSECallbacks,
  options: ChatOptions = {},
): void {
  abortStream();

  const controller = new AbortController();
  _currentAbortController = controller;

  const body: Record<string, unknown> = { conversation_id: conversationId, decisions };
  if (options.model) body.model = options.model;
  if (options.capabilities) body.capabilities = options.capabilities;
  if (options.yolo) body.yolo = true;

  fetch(`${BASE}/chat/resume`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal: controller.signal,
  })
    .then(async (res) => {
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: '请求失败' }));
        callbacks.onError?.(err.detail || '请求失败');
        return;
      }
      handleSSEStream(res, callbacks);
    })
    .catch((err) => {
      if (err.name === 'AbortError') return;
      callbacks.onError?.(err.message);
    });
}

// ===== Files =====

export async function listFiles(path = '/'): Promise<FileItem[]> {
  return request('GET', `/files?path=${encodeURIComponent(path)}`);
}

export interface FileIndexEntry {
  path: string;
  name: string;
  is_dir: boolean;
  size: number;
  modified_at: string;
}

export interface FileIndexResponse {
  entries: FileIndexEntry[];
  truncated: boolean;
  root: string;
}

/** Flat recursive listing of the user filesystem, used by the chat input
 *  @-mention picker for in-memory fuzzy matching. Cached client-side so
 *  successive @ presses don't re-fetch. */
export async function listFileIndex(root = '/', includeDirs = true): Promise<FileIndexResponse> {
  const qs = `root=${encodeURIComponent(root)}&include_dirs=${includeDirs}`;
  return request('GET', `/files/index?${qs}`);
}

export async function readFile(path: string): Promise<{ content: string }> {
  return request('GET', `/files/read?path=${encodeURIComponent(path)}`);
}

export async function writeFile(path: string, content: string): Promise<void> {
  return request('POST', '/files/write', { path, content });
}

export async function editFile(path: string, oldString: string, newString: string): Promise<void> {
  return request('PUT', '/files/edit', { path, old_string: oldString, new_string: newString });
}

export async function deleteFile(path: string): Promise<void> {
  return request('DELETE', `/files?path=${encodeURIComponent(path)}`);
}

export async function moveFile(source: string, destination: string): Promise<void> {
  return request('POST', '/files/move', { source, destination });
}

export async function copyFile(source: string, destination: string): Promise<void> {
  return request('POST', '/files/copy', { source, destination });
}

/** Build a download URL for one or more files/folders zipped on the server.
 *  Uses the token-in-query variant so a plain `<a>` click triggers download.
 *  For multi-select downloads (`paths.length > 1`) the server names the
 *  archive `files.zip`; single-path downloads inherit the basename. */
export function zipDownloadUrl(paths: string[]): string {
  const pathsJson = JSON.stringify(paths);
  return `${BASE}/files/zip-token?paths=${encodeURIComponent(pathsJson)}&token=${encodeURIComponent(getToken())}`;
}

export async function uploadFiles(path: string, files: File[], keepStructure = false): Promise<void> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const qs = `path=${encodeURIComponent(path)}${keepStructure ? '&keep_structure=true' : ''}`;
  return request('POST', `/files/upload?${qs}`, formData);
}

/** Upload progress event payload — used by uploadFilesWithProgress.
 *  - loaded / total: bytes uploaded vs total in the **current batch** (chunk-aware)
 *  - batchIndex / batchCount: which chunk of the multi-batch upload (1-indexed)
 *  - cumulativeLoaded / cumulativeTotal: bytes across the entire multi-file
 *    upload (sum of finished batches + current batch progress)
 *  - currentFileName: heuristic — the first file's name in the current batch */
export interface UploadProgressEvent {
  loaded: number;
  total: number;
  batchIndex: number;
  batchCount: number;
  cumulativeLoaded: number;
  cumulativeTotal: number;
  currentFileName: string;
  fileCount: number;
}

/** Upload files with byte-level progress tracking (XMLHttpRequest under the hood
 *  since fetch doesn't expose upload.progress without ReadableStream support).
 *
 *  Large uploads are auto-chunked into batches of MAX_BATCH_BYTES / MAX_BATCH_FILES
 *  to avoid backend body-size limits and to give incremental progress feedback
 *  even when the user uploads e.g. 500 small files at once.
 *
 *  onProgress is called: once per progress tick during XHR upload (frequent),
 *  plus once at the start/end of each batch. Caller should throttle UI updates. */
export async function uploadFilesWithProgress(
  path: string,
  files: File[],
  keepStructure: boolean,
  onProgress?: (e: UploadProgressEvent) => void,
): Promise<void> {
  if (files.length === 0) return;

  const MAX_BATCH_BYTES = 50 * 1024 * 1024;  // 50 MB per batch
  const MAX_BATCH_FILES = 50;                 // hard cap per batch

  // Group files into batches honouring both byte and count caps. Files larger
  // than MAX_BATCH_BYTES go alone in their own batch (no point splitting one).
  const batches: File[][] = [];
  let cur: File[] = [];
  let curBytes = 0;
  for (const f of files) {
    const sz = f.size || 0;
    if (
      cur.length > 0 &&
      (cur.length >= MAX_BATCH_FILES || curBytes + sz > MAX_BATCH_BYTES)
    ) {
      batches.push(cur);
      cur = [];
      curBytes = 0;
    }
    cur.push(f);
    curBytes += sz;
  }
  if (cur.length > 0) batches.push(cur);

  const cumulativeTotal = files.reduce((s, f) => s + (f.size || 0), 0);
  let cumulativeLoaded = 0;

  for (let i = 0; i < batches.length; i++) {
    const batch = batches[i];
    const batchTotal = batch.reduce((s, f) => s + (f.size || 0), 0);
    const batchStartCumulative = cumulativeLoaded;

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      for (const file of batch) {
        formData.append('files', file);
      }
      const qs = `path=${encodeURIComponent(path)}${keepStructure ? '&keep_structure=true' : ''}`;

      xhr.open('POST', `${BASE}/files/upload?${qs}`);
      xhr.setRequestHeader('Authorization', `Bearer ${getToken()}`);

      xhr.upload.onprogress = (evt) => {
        if (!onProgress) return;
        const loaded = evt.lengthComputable ? evt.loaded : 0;
        const total = evt.lengthComputable ? evt.total : batchTotal;
        onProgress({
          loaded,
          total,
          batchIndex: i + 1,
          batchCount: batches.length,
          cumulativeLoaded: batchStartCumulative + loaded,
          cumulativeTotal,
          currentFileName: batch[0]?.name || '',
          fileCount: files.length,
        });
      };

      xhr.onload = () => {
        if (xhr.status === 401) {
          if (getToken()) { clearToken(); window.location.reload(); }
          reject(new Error('认证失败，请重新登录'));
          return;
        }
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          let detail = '上传失败';
          try {
            const j = JSON.parse(xhr.responseText);
            detail = j?.detail || detail;
          } catch { /* not json */ }
          reject(new Error(detail));
        }
      };
      xhr.onerror = () => reject(new Error('网络错误，上传失败'));
      xhr.onabort = () => reject(new Error('上传已取消'));

      xhr.send(formData);
    });

    cumulativeLoaded = batchStartCumulative + batchTotal;
    // Emit a final tick at batch boundary so the bar visibly advances even
    // when a batch finishes faster than the next progress event.
    onProgress?.({
      loaded: batchTotal,
      total: batchTotal,
      batchIndex: i + 1,
      batchCount: batches.length,
      cumulativeLoaded,
      cumulativeTotal,
      currentFileName: batch[0]?.name || '',
      fileCount: files.length,
    });
  }
}

export async function downloadFile(path: string): Promise<Response> {
  const res = await fetch(`${BASE}/files/download?path=${encodeURIComponent(path)}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!res.ok) throw new Error('下载失败');
  return res;
}

export function mediaUrl(path: string): string {
  return `${BASE}/files/media?path=${encodeURIComponent(path)}&token=${encodeURIComponent(getToken())}`;
}

export function attachmentUrl(convId: string, path: string): string {
  return `${BASE}/conversations/${convId}/attachments/${encodeURIComponent(path)}`;
}

// ===== System Prompt =====

export async function getSystemPrompt(): Promise<SystemPromptResponse> {
  return request('GET', '/system-prompt');
}

export async function updateSystemPrompt(prompt: string): Promise<void> {
  return request('PUT', '/system-prompt', { prompt });
}

export async function resetSystemPrompt(): Promise<{ prompt: string }> {
  return request('DELETE', '/system-prompt');
}

export async function listPromptVersions(): Promise<PromptVersion[]> {
  return request('GET', '/system-prompt/versions');
}

export async function savePromptVersion(
  content: string,
  label = '',
  note = '',
): Promise<void> {
  return request('POST', '/system-prompt/versions', { content, label, note });
}

export async function getPromptVersion(versionId: string): Promise<PromptVersion & { content: string }> {
  return request('GET', `/system-prompt/versions/${versionId}`);
}

export async function updatePromptVersionMeta(
  versionId: string,
  label?: string,
  note?: string,
): Promise<void> {
  return request('PUT', `/system-prompt/versions/${versionId}`, { label, note });
}

export async function deletePromptVersion(versionId: string): Promise<void> {
  return request('DELETE', `/system-prompt/versions/${versionId}`);
}

export async function rollbackPromptVersion(versionId: string): Promise<{ prompt: string }> {
  return request('POST', `/system-prompt/versions/${versionId}/rollback`);
}

// ===== User Profile =====

export async function getUserProfile(): Promise<{ profile: UserProfile }> {
  return request('GET', '/user-profile');
}

export async function updateUserProfile(profile: UserProfile): Promise<void> {
  return request('PUT', '/user-profile', profile);
}

export async function getAgentNotes(): Promise<{ content: string; locked: boolean }> {
  return request('GET', '/user-profile/agent-notes');
}

export async function updateAgentNotes(content: string, locked: boolean): Promise<void> {
  return request('PUT', '/user-profile/agent-notes', { content, locked });
}

export async function listProfileVersions(): Promise<PromptVersion[]> {
  return request('GET', '/user-profile/versions');
}

export async function getProfileVersion(versionId: string): Promise<{ id: string; content: string; label: string; note: string }> {
  return request('GET', `/user-profile/versions/${versionId}`);
}

export async function updateProfileVersionMeta(
  versionId: string,
  label?: string,
  note?: string,
): Promise<void> {
  return request('PUT', `/user-profile/versions/${versionId}`, { label, note });
}

export async function deleteProfileVersion(versionId: string): Promise<void> {
  return request('DELETE', `/user-profile/versions/${versionId}`);
}

export async function rollbackProfileVersion(versionId: string): Promise<{ content: string }> {
  return request('POST', `/user-profile/versions/${versionId}/rollback`);
}

// ===== Scripts =====

export async function runScript(
  scriptPath: string,
  args: string[] | null = null,
  inputData: string | null = null,
  timeout = 30,
): Promise<unknown> {
  return request('POST', '/scripts/run', {
    script_path: scriptPath,
    args,
    input_data: inputData,
    timeout,
  });
}

// ===== Models =====

export async function getModels(): Promise<ModelsResponse> {
  return request('GET', '/models');
}

export async function getModelVisibility(): Promise<ModelVisibilityResponse> {
  return request('GET', '/models/visibility');
}

export async function toggleModelVisibility(modelId: string, enabled: boolean): Promise<{ success: boolean; hidden: string[] }> {
  return request('PUT', '/models/visibility', { toggle: { id: modelId, enabled } });
}

// ===== Audio =====

export async function transcribeAudio(
  audioBlob: Blob,
  filename = 'recording.webm',
): Promise<{ text: string }> {
  const formData = new FormData();
  formData.append('file', audioBlob, filename);

  const res = await fetch(`${BASE}/audio/transcribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || '语音识别失败');
  }
  return res.json();
}

// ===== Subagents =====

export async function listSubagents(): Promise<SubagentListResponse> {
  return request('GET', '/subagents');
}

export async function addSubagent(config: Partial<SubagentConfig>): Promise<void> {
  return request('POST', '/subagents', config);
}

export async function getSubagent(id: string): Promise<SubagentConfig> {
  return request('GET', `/subagents/${id}`);
}

export async function updateSubagent(id: string, updates: Partial<SubagentConfig>): Promise<void> {
  return request('PUT', `/subagents/${id}`, updates);
}

export async function deleteSubagent(id: string): Promise<void> {
  return request('DELETE', `/subagents/${id}`);
}

// ===== Capability Prompts =====

export interface CapabilityPromptItem {
  key: string;
  default: string;
  custom: string | null;
}

export async function getCapabilityPrompts(): Promise<{ prompts: CapabilityPromptItem[] }> {
  return request('GET', '/capability-prompts');
}

export async function updateCapabilityPrompt(key: string, text: string): Promise<void> {
  return request('PUT', `/capability-prompts/${key}`, { text });
}

export async function resetCapabilityPrompt(key: string): Promise<void> {
  return request('DELETE', `/capability-prompts/${key}`);
}

// ===== Soul Config =====

export interface SoulConfig {
  memory_enabled: boolean;
  include_consumer_conversations: boolean;
  max_recent_messages: number;
  memory_subagent_enabled: boolean;
  soul_edit_enabled: boolean;
}

export async function getSoulConfig(): Promise<SoulConfig> {
  return request('GET', '/soul/config');
}

export async function updateSoulConfig(updates: Partial<SoulConfig>): Promise<{ success: boolean; config: SoulConfig }> {
  return request('PUT', '/soul/config', updates);
}

// ===== Python Packages =====

export interface PackageInfo {
  name: string;
  version: string;
}

export async function listPackages(): Promise<{ packages: PackageInfo[]; venv_ready: boolean }> {
  return request('GET', '/packages');
}

export async function initVenv(): Promise<{ success: boolean }> {
  return request('POST', '/packages/init');
}

export async function installPackage(pkg: string): Promise<{ success: boolean; output: string }> {
  return request('POST', '/packages/install', { package: pkg });
}

export async function uninstallPackage(pkg: string): Promise<{ success: boolean; output: string }> {
  return request('POST', '/packages/uninstall', { package: pkg });
}

// ===== Batch Execution =====

export async function uploadBatchExcel(file: File): Promise<BatchUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${BASE}/batch/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || '上传失败');
  }
  return res.json();
}

export async function startBatchRun(config: BatchRunConfig): Promise<{ task_id: string; total: number }> {
  return request('POST', '/batch/run', config);
}

export async function listBatchTasks(): Promise<BatchTask[]> {
  return request('GET', '/batch/tasks');
}

export async function getBatchTask(taskId: string): Promise<BatchTask> {
  return request('GET', `/batch/tasks/${taskId}`);
}

export async function cancelBatchTask(taskId: string): Promise<void> {
  return request('POST', `/batch/tasks/${taskId}/cancel`);
}

export function batchDownloadUrl(taskId: string): string {
  return `${BASE}/batch/tasks/${taskId}/download?token=${encodeURIComponent(getToken())}`;
}

// ===== Scheduler =====

export async function listSchedulerTasks(): Promise<unknown[]> {
  return request('GET', '/scheduler');
}

export async function getSchedulerTask(taskId: string): Promise<unknown> {
  return request('GET', `/scheduler/${taskId}`);
}

export async function createSchedulerTask(config: unknown): Promise<unknown> {
  return request('POST', '/scheduler', config);
}

export async function updateSchedulerTask(taskId: string, config: unknown): Promise<void> {
  return request('PUT', `/scheduler/${taskId}`, config);
}

export async function deleteSchedulerTask(taskId: string): Promise<void> {
  return request('DELETE', `/scheduler/${taskId}`);
}

export async function getSchedulerRuns(taskId: string): Promise<unknown[]> {
  return request('GET', `/scheduler/${taskId}/runs`);
}

export async function runSchedulerTaskNow(taskId: string): Promise<void> {
  return request('POST', `/scheduler/${taskId}/run-now`);
}

// v2 spawn-tree endpoints (admin scope)
// `service_id` switches to the matching `/services/{service_id}/...` mirror.
export async function getSchedulerTaskTree(
  taskId: string, opts?: { maxDepth?: number; serviceId?: string },
): Promise<unknown> {
  const max = opts?.maxDepth ?? 5;
  const base = opts?.serviceId
    ? `/scheduler/services/${opts.serviceId}/${taskId}/tree`
    : `/scheduler/${taskId}/tree`;
  return request('GET', `${base}?max_depth=${max}`);
}

export async function getSchedulerTaskChildren(
  taskId: string, opts?: { serviceId?: string },
): Promise<unknown[]> {
  const base = opts?.serviceId
    ? `/scheduler/services/${opts.serviceId}/${taskId}/children`
    : `/scheduler/${taskId}/children`;
  return request('GET', base);
}

export async function getSchedulerTaskAncestors(
  taskId: string, opts?: { serviceId?: string },
): Promise<unknown[]> {
  const base = opts?.serviceId
    ? `/scheduler/services/${opts.serviceId}/${taskId}/ancestors`
    : `/scheduler/${taskId}/ancestors`;
  return request('GET', base);
}

export async function getSchedulerChainQuota(
  rootTaskId: string, opts?: { serviceId?: string },
): Promise<unknown> {
  const base = opts?.serviceId
    ? `/scheduler/services/${opts.serviceId}/quotas/${rootTaskId}`
    : `/scheduler/quotas/${rootTaskId}`;
  return request('GET', base);
}

export async function migrateAdminTasksV1ToV2(
  dryRun: boolean,
): Promise<unknown> {
  return request('POST', '/scheduler/admin/migrate', { dry_run: dryRun });
}

// ===== Services =====

export async function listServices(): Promise<unknown[]> {
  return request('GET', '/services');
}

export async function getService(serviceId: string): Promise<unknown> {
  return request('GET', `/services/${serviceId}`);
}

export async function createService(config: unknown): Promise<unknown> {
  return request('POST', '/services', config);
}

export async function updateService(serviceId: string, config: unknown): Promise<void> {
  return request('PUT', `/services/${serviceId}`, config);
}

export async function deleteService(serviceId: string): Promise<void> {
  return request('DELETE', `/services/${serviceId}`);
}

export async function listServiceKeys(serviceId: string): Promise<unknown[]> {
  return request('GET', `/services/${serviceId}/keys`);
}

export async function createServiceKey(
  serviceId: string,
  name: string,
): Promise<{ key: string }> {
  return request('POST', `/services/${serviceId}/keys`, { name });
}

export async function deleteServiceKey(
  serviceId: string,
  keyId: string,
): Promise<void> {
  return request('DELETE', `/services/${serviceId}/keys/${keyId}`);
}

// 使用情况：consumer 会话历史 + API 调用记录

export interface ServiceConvSummary {
  id: string;
  title: string;
  source: string;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface ServiceConvDetail extends ServiceConvSummary {
  messages: Array<{
    role: string;
    content: string;
    timestamp?: string;
    tool_calls?: unknown;
    attachments?: unknown;
    blocks?: unknown;
  }>;
}

export interface ServiceUsageRecord {
  ts: string;
  channel: 'web' | 'api' | 'wechat' | string;
  key_id: string;
  conv_id: string;
  endpoint: string;
  status_code: number;
  latency_ms: number;
  ok: boolean;
}

export async function listServiceConversations(
  serviceId: string,
): Promise<ServiceConvSummary[]> {
  return request('GET', `/services/${serviceId}/conversations`);
}

export async function getServiceConversation(
  serviceId: string,
  convId: string,
): Promise<ServiceConvDetail> {
  return request('GET', `/services/${serviceId}/conversations/${convId}`);
}

export async function deleteServiceConversation(
  serviceId: string,
  convId: string,
): Promise<void> {
  return request('DELETE', `/services/${serviceId}/conversations/${convId}`);
}

export async function listServiceUsage(
  serviceId: string,
  opts?: { limit?: number; channel?: 'web' | 'api' | 'wechat' },
): Promise<{ records: ServiceUsageRecord[]; limit: number; channel: string | null }> {
  const params = new URLSearchParams();
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.channel) params.set('channel', opts.channel);
  const qs = params.toString();
  return request('GET', `/services/${serviceId}/usage${qs ? `?${qs}` : ''}`);
}

// ===== Inbox =====

export interface InboxMessage {
  id: string;
  service_id: string;
  service_name: string;
  conversation_id: string;
  wechat_session_id?: string;
  wechat_user_id?: string;
  message: string;
  timestamp: string;
  status: 'unread' | 'read' | 'handled';
  handled_by: 'agent' | 'manual' | null;
  agent_response: string | null;
}

export async function listInbox(status?: string): Promise<{ messages: InboxMessage[]; unread_count: number }> {
  const params = status ? `?status=${status}` : '';
  return request('GET', `/inbox${params}`);
}

export async function getInboxUnreadCount(): Promise<{ count: number }> {
  return request('GET', '/inbox/unread-count');
}

export async function getInboxMessage(msgId: string): Promise<InboxMessage> {
  return request('GET', `/inbox/${msgId}`);
}

export async function updateInboxStatus(msgId: string, status: string): Promise<InboxMessage> {
  return request('PUT', `/inbox/${msgId}`, { status });
}

export async function deleteInboxMessage(msgId: string): Promise<void> {
  return request('DELETE', `/inbox/${msgId}`);
}

// ===== API Keys (per-user) =====

export interface ApiKeysStatus {
  has_llm: boolean;
  has_openai: boolean;
  has_anthropic: boolean;
}

export interface ApiKeysMasked {
  openai_api_key: string;
  openai_api_key_configured: boolean;
  openai_base_url: string;
  anthropic_api_key: string;
  anthropic_api_key_configured: boolean;
  anthropic_base_url: string;
  tavily_api_key: string;
  tavily_api_key_configured: boolean;
  credential_sources?: Record<string, 'platform' | 'user'>;
  platform_configured?: Record<string, boolean>;
  [key: string]: string | boolean | Record<string, string> | Record<string, boolean> | undefined;
}

export interface ApiKeysTestResult {
  results: Record<string, { ok: boolean; status?: number; error?: string }>;
}

export async function getApiKeys(): Promise<ApiKeysMasked> {
  return request('GET', '/settings/api-keys');
}

export async function updateApiKeys(
  keys: Record<string, string | Record<string, string>>,
): Promise<{ success: boolean; keys: ApiKeysMasked }> {
  return request('PUT', '/settings/api-keys', keys);
}

export async function testApiKeys(provider: string): Promise<ApiKeysTestResult> {
  return request('POST', '/settings/api-keys/test', { provider });
}

export async function getApiKeysStatus(): Promise<ApiKeysStatus> {
  return request('GET', '/settings/api-keys/status');
}

// ===== OpenRouter whitelist =====

export interface OpenRouterEnabledModel {
  id: string;
  name: string;
  reasoning: boolean;
}

export interface OpenRouterRemoteModel {
  id: string;
  name?: string;
  description?: string;
  context_length?: number;
  supported_parameters?: string[];
  architecture?: { modality?: string; input_modalities?: string[]; output_modalities?: string[] };
  [key: string]: unknown;
}

export async function getOpenRouterEnabledModels(): Promise<{ models: OpenRouterEnabledModel[] }> {
  return request('GET', '/settings/openrouter/enabled-models');
}

export async function setOpenRouterEnabledModels(
  models: OpenRouterEnabledModel[],
): Promise<{ success: boolean; models: OpenRouterEnabledModel[] }> {
  return request('PUT', '/settings/openrouter/enabled-models', { models });
}

/** Prefer browser → OpenRouter; fall back to backend proxy on CORS/network failure. */
export async function fetchOpenRouterRemoteModels(): Promise<OpenRouterRemoteModel[]> {
  const parse = (data: unknown): OpenRouterRemoteModel[] => {
    const list = (data as { data?: OpenRouterRemoteModel[] })?.data;
    return Array.isArray(list) ? list : [];
  };
  try {
    const resp = await fetch('https://openrouter.ai/api/v1/models?output_modalities=text', {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (resp.ok) return parse(await resp.json());
  } catch {
    /* CORS / network — proxy below */
  }
  const proxied = await request<unknown>('GET', '/settings/openrouter/remote-models');
  return parse(proxied);
}

// ===== Preferences =====

export interface UserPreferences {
  tz_offset_hours: number;
  /** UI/messages language. ``""`` means "follow Accept-Language" (auto). */
  language?: '' | 'zh' | 'en';
}

export async function getPreferences(): Promise<UserPreferences> {
  return request('GET', '/preferences');
}

export async function updatePreferences(data: Partial<UserPreferences>): Promise<UserPreferences> {
  return request('PUT', '/preferences', data);
}

export async function getServerTime(): Promise<{ server_time: string }> {
  return request('GET', '/server-time');
}

// ===== Backup & Restore (per-user) =====

export interface BackupModule {
  id: string;
  label: string;
}

export interface BackupModulesResp {
  modules: BackupModule[];
  default_selected: string[];
}

export interface BackupPreviewResp {
  modules: Record<string, { file_count: number; total_bytes: number }>;
  total_file_count: number;
  total_uncompressed_bytes: number;
  selection: string[];
}

export interface BackupImportResp {
  ok: boolean;
  mode: 'merge' | 'overwrite';
  files_written: number;
  files_skipped: number;
  api_keys_imported: number;
  snapshot_path: string | null;
  warnings: string[];
}

export async function listBackupModules(): Promise<BackupModulesResp> {
  return request('GET', '/backup/modules');
}

export async function previewBackup(opts: {
  modules: string[];
  includeMedia: boolean;
  includeApiKeys: boolean;
}): Promise<BackupPreviewResp> {
  const fd = new FormData();
  fd.append('modules', opts.modules.join(','));
  fd.append('include_media', String(opts.includeMedia));
  fd.append('include_api_keys', String(opts.includeApiKeys));
  return request('POST', '/backup/preview', fd);
}

/**
 * Trigger a streaming download of the user backup ZIP.
 * Returns the suggested filename + size in bytes.
 */
export async function downloadBackup(opts: {
  modules: string[];
  includeMedia: boolean;
  includeApiKeys: boolean;
}): Promise<{ filename: string; sizeBytes: number; fileCount: number }> {
  const fd = new FormData();
  fd.append('modules', opts.modules.join(','));
  fd.append('include_media', String(opts.includeMedia));
  fd.append('include_api_keys', String(opts.includeApiKeys));

  const res = await fetch(`${BASE}/backup/export`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try { const j = await res.json(); detail = j.detail || detail; } catch { /* noop */ }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m ? m[1] : `openjellyfish-backup-${Date.now()}.zip`;
  const fileCount = parseInt(res.headers.get('X-Backup-File-Count') || '0', 10);

  // Trigger browser save dialog.
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return { filename, sizeBytes: blob.size, fileCount };
}

export async function importBackup(opts: {
  file: File;
  mode: 'merge' | 'overwrite';
  password?: string;
  modules?: string[];
}): Promise<BackupImportResp> {
  const fd = new FormData();
  fd.append('file', opts.file);
  fd.append('mode', opts.mode);
  if (opts.password) fd.append('password', opts.password);
  if (opts.modules && opts.modules.length) fd.append('modules', opts.modules.join(','));
  return request('POST', '/backup/import', fd);
}

// ── LiveKit 实时语音前台 ────────────────────────────────────────────

export interface VoiceLiveStatus {
  configured: boolean;
  url: string;
}

export interface VoiceLiveToken {
  url: string;
  token: string;
  room: string;
  identity: string;
}

export interface VoiceAgentConfig {
  enabled: boolean;
  greeting: string;
  system_prompt: string;
  routing_policy: string;
  fillers: Record<string, string[]>;
  interruption: { allow_interruptions: boolean; min_interruption_words: number };
  providers: {
    stt: string;
    stt_model: string;
    aliyun_vocabulary_id?: string;
    llm?: string;
    llm_model: string;
    tts: string;
    tts_model?: string;
    tts_voice: string;
    fish_reference_id?: string;
    fish_voices?: Array<{ id: string; label: string }>;
    aliyun_tts_voice?: string;
  };
}

export async function getVoiceLiveStatus(): Promise<VoiceLiveStatus> {
  return request('GET', '/voice/live/status');
}

export async function getVoiceLiveToken(
  conversationId: string,
  model?: string,
  capabilities?: string[],
): Promise<VoiceLiveToken> {
  return request('POST', '/voice/live/token', {
    conversation_id: conversationId,
    model,
    capabilities: capabilities || [],
  });
}

export async function getVoiceAgentConfig(): Promise<VoiceAgentConfig> {
  return request('GET', '/voice/live/config');
}

export async function updateVoiceAgentConfig(
  updates: Partial<VoiceAgentConfig>,
): Promise<VoiceAgentConfig> {
  return request('PUT', '/voice/live/config', updates);
}

export async function resetVoiceAgentConfig(): Promise<VoiceAgentConfig> {
  return request('DELETE', '/voice/live/config');
}

// ── Token Usage ──────────────────────────────────────────────────
export interface UsageBucket {
  calls: number;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

export interface UsageRow extends UsageBucket {
  name: string;
  id?: string;
}

export interface UsageSummary {
  total: UsageBucket;
  months_scanned: number;
  by_model: UsageRow[];
  by_service: UsageRow[];
  by_key: UsageRow[];
  by_provider: UsageRow[];
  by_channel: UsageRow[];
  by_day: UsageRow[];
}

export async function getUsageSummary(months = 3): Promise<UsageSummary> {
  return request('GET', `/usage/summary?months=${encodeURIComponent(months)}`);
}

/** 单个 service 的 token 用量聚合（admin 在「服务页」查看自己某服务的用量）。 */
export async function getServiceTokenUsage(serviceId: string, months = 3): Promise<UsageSummary> {
  return request('GET', `/services/${encodeURIComponent(serviceId)}/token-usage?months=${encodeURIComponent(months)}`);
}
